import { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Check, ChevronLeft, ChevronRight, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { Alert, AlertImage } from "@/alerts/types/machine-alert";
import { availableVerdicts, type AlertVerdict } from "@/alerts/utils/alert-decision";
import type { AlertStatus } from "@/alerts/types/machine-alert";
import type { CloseDispositionRead } from "@/alerts/types/alerts-workspace";
import { primaryImageLabel } from "@/alerts/types/machine-alert";
import { labelConfidencePercent } from "@/shared/utils/confidence";
import { formatOrgLocaleTime } from "@/shared/utils/date/org-time";
import { cn } from "@/ui/lib/utils";
import AlertReviewStrip, { type StripItem } from "./AlertReviewStrip";
import {
  clampPanelSize,
  useAlertReviewLayoutStore,
} from "@/map/stores/useAlertReviewLayoutStore";

interface AlertReviewV2Props {
  alert: Alert;
  /** Frames of the event, lifted to the page (shared with the keyboard hook). */
  images: AlertImage[];
  imageIndex: number;
  onImageIndexChange: (index: number) => void;
  /** `image_id` of the frame the detection fired on, or null. */
  alertFrameId: number | null;
  imagesLoading?: boolean;
  machineName: string;
  /** True = the detection is real. False = a false positive. */
  onTrue: () => void;
  onFalse: () => void;
  isUpdating?: boolean;
  /**
   * The alert's CANONICAL result, from detail — not from the list row, which can
   * be minutes old and is exactly what MIN-270 is about. Null while detail
   * loads; the controls then offer nothing rather than guessing.
   */
  currentStatus?: AlertStatus | null;
  currentDisposition?: CloseDispositionRead | null;
  /**
   * The alert changed underneath the operator. Shown instead of letting them
   * press again — the state they judged is no longer the state on the server.
   */
  conflictMessage?: string | null;
  onDismissConflict?: () => void;
  /** Step to the neighbouring alert without leaving review. */
  onPrev?: () => void;
  onNext?: () => void;
  /** Position in the feed, for the "Alert n of N" readout. Hidden when absent. */
  position?: { index: number; total: number } | null;
  onClose?: () => void;
}

/**
 * Alert Review V2 — the review laid over the live map.
 *
 * Two equal panels, camera on the left and the alert frame on the right, with a
 * corridor of MAP between them: the alert's GPS marker sits in that gap, which
 * is the reason this is an overlay and not a page. The frames of the event run
 * down the right edge; the verdict is one oversized button in the middle, so
 * dismissing an alert needs a click, not aim.
 */
export const AlertReviewV2 = ({
  alert,
  images,
  imageIndex,
  onImageIndexChange,
  alertFrameId,
  imagesLoading = false,
  machineName,
  onTrue,
  onFalse,
  isUpdating = false,
  currentStatus = null,
  currentDisposition = null,
  conflictMessage = null,
  onDismissConflict,
  onPrev,
  onNext,
  position = null,
  onClose,
}: AlertReviewV2Props) => {
  const { t } = useTranslation("map");
  const stageRef = useRef<HTMLDivElement>(null);
  const panelSize = useAlertReviewLayoutStore((s) => s.alertSize);
  const videoWidth = useAlertReviewLayoutStore((s) => s.videoSize.width);
  const setPanelSize = useAlertReviewLayoutStore((s) => s.setPanelSize);
  const resetPanelSizes = useAlertReviewLayoutStore((s) => s.resetPanelSizes);

  // Re-clamp against the stage the moment it is measurable and on every resize:
  // a size stored on a wide monitor would otherwise close the corridor on a
  // laptop, and the corridor is the point.
  const [stage, setStage] = useState<{ width: number; height: number } | null>(null);
  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const read = () => setStage({ width: el.clientWidth, height: el.clientHeight });
    read();
    const observer = new ResizeObserver(read);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const applySize = useCallback(
    (next: { width: number; height: number }) => {
      if (!stage) return;
      // Growing this panel is paid for by width, never by the corridor — so the
      // clamp has to know what the camera opposite already takes.
      setPanelSize("alert", clampPanelSize(next, stage, videoWidth));
    },
    [stage, setPanelSize, videoWidth],
  );

  useEffect(() => {
    if (stage) applySize(panelSize);
    // Only when the stage changes — applying on every panelSize change would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  const safeIndex = images.length > 0 ? Math.min(Math.max(imageIndex, 0), images.length - 1) : 0;
  const currentImage: AlertImage | null = images[safeIndex] ?? null;
  const label = currentImage ? primaryImageLabel(currentImage) : undefined;
  const confidence = labelConfidencePercent(currentImage?.labels);

  const frames: StripItem[] = images.map((image) => ({
    key: String(image.image_id),
    src: image.signed_url,
    label: formatOrgLocaleTime(image.timestamp, {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }),
    isAlertFrame: alertFrameId != null && image.image_id === alertFrameId,
  }));

  const width = panelSize.width;
  const height = panelSize.height;

  return (
    <div
      ref={stageRef}
      // z-30, above the timeline shell's z-20. The verdict dock inside carries a
      // z-index of its own, but this element creates the stacking context it
      // lives in, so raising the CHILD could never lift it past a sibling of
      // this one — the ceiling is here.
      className="pointer-events-none absolute inset-x-5 bottom-[186px] top-16 z-30 flex items-start justify-between"
    >
      {/* The camera side is `MapVideoPanel` in its review layout. It is NOT
          rendered here: it has to stay inside the timeline provider that owns
          the shared playback cursor, so instead both sides read the same size
          from `useAlertReviewLayoutStore` and cannot drift apart. This spacer
          reserves its width so `justify-between` puts the corridor — the strip
          of MAP holding this alert's own GPS marker — dead centre. */}
      <div aria-hidden="true" style={{ width: videoWidth }} className="shrink-0" />

      <div className="pointer-events-auto flex items-start gap-2.5">
        <section
          className="relative overflow-hidden rounded-lg border border-border bg-black shadow-lg"
          style={{ width, height }}
          aria-label={t("alertReview.panelLabel")}
        >
          {currentImage ? (
            <img
              src={currentImage.signed_url}
              alt={t("alertReview.frameAlt", {
                index: safeIndex + 1,
                total: images.length,
              })}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-xs text-white/60">
              {imagesLoading ? t("alertReview.loadingFrames") : t("alertReview.noImage")}
            </div>
          )}

          {/* No detection box, by decision. The design sketched one, but the API
              carries no coordinates — not on `AlertImage`, not on
              `AlertPreviewLabel`, and no bbox field in the spec — and drawing an
              invented box over a real photo would assert something the model
              never said. Label and confidence ride as chips instead. Deliberate,
              not an oversight: revisit only if the backend starts sending them. */}

          <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/60 to-transparent" />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/60 to-transparent" />

          <div className="absolute left-3 top-3 flex flex-col items-start gap-1.5">
            <span className="flex items-center gap-1.5 rounded-md bg-black/75 px-2 py-1 text-[13px] font-semibold text-white">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
              {label?.final_label ?? label?.classification ?? alert.type}
              {confidence != null ? (
                <span className="font-mono tabular-nums text-amber-400">{confidence}%</span>
              ) : null}
            </span>
          </div>

          <div className="absolute right-3 top-3 flex flex-col items-end gap-1.5">
            <span className="rounded-md bg-black/75 px-2 py-1 text-xs text-white">
              {machineName}
            </span>
            <span className="rounded-md bg-black/75 px-2 py-1 font-mono text-xs tabular-nums text-white">
              {formatOrgLocaleTime(alert.timestamp, {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              })}
            </span>
          </div>

          <div className="absolute inset-x-3 bottom-3 flex items-end justify-between gap-2">
            <span className="flex items-center gap-1.5 rounded-md bg-black/75 px-2 py-1 text-xs text-white">
              {onPrev ? (
                <NavArrow onClick={onPrev} label={t("alertReview.previousAlert")}>
                  <ChevronLeft className="h-3.5 w-3.5" />
                </NavArrow>
              ) : null}
              {t("alertReview.alertId", { id: alert.id })}
              {position ? (
                <span className="font-mono tabular-nums text-white/70">
                  {position.index + 1} / {position.total}
                </span>
              ) : null}
              {onNext ? (
                <NavArrow onClick={onNext} label={t("alertReview.nextAlert")}>
                  <ChevronRight className="h-3.5 w-3.5" />
                </NavArrow>
              ) : null}
            </span>
            {images.length > 0 ? (
              <span className="rounded-md bg-black/75 px-2 py-1 font-mono text-xs tabular-nums text-white">
                {t("alertReview.frameCounter", {
                  index: safeIndex + 1,
                  total: images.length,
                })}
              </span>
            ) : null}
          </div>

          <ResizeHandle
            t={t}
            onResize={(dx, dy) => applySize({ width: width - dx, height: height + dy })}
            onReset={resetPanelSizes}
          />
        </section>

        <AlertReviewStrip
          items={frames}
          activeIndex={safeIndex}
          onSelect={onImageIndexChange}
          label={t("alertReview.eventFrames")}
          side="right"
          className="pointer-events-auto"
          // The strip is as tall as the panel it belongs to.
          style={{ height }}
        />
      </div>

      {onClose ? (
        <button
          type="button"
          onClick={onClose}
          className="pointer-events-auto absolute right-0 top-0 rounded-md border border-border bg-card px-2 py-1 text-xs text-muted-foreground shadow hover:text-foreground"
        >
          {t("alertReview.close")}
        </button>
      ) : null}

      <VerdictDock
        t={t}
        // Only the verdict this alert does NOT already carry: re-submitting the
        // one it has is not a correction, and the workflow rejects it.
        offered={currentStatus ? availableVerdicts(currentStatus, currentDisposition) : []}
        onTrue={onTrue}
        onFalse={onFalse}
        disabled={isUpdating}
        conflictMessage={conflictMessage}
        onDismissConflict={onDismissConflict}
      />
    </div>
  );
};

const NavArrow = ({
  onClick,
  label,
  children,
}: {
  onClick: () => void;
  label: string;
  children: ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className="rounded p-0.5 text-white/70 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-400"
  >
    {children}
  </button>
);

/**
 * The verdict. Oversized and centred on purpose: an operator clearing a queue
 * should not have to aim, and the two answers carry equal weight — "the model
 * was right" is not a happier outcome than "the model was wrong".
 */
const VerdictDock = ({
  t,
  offered,
  onTrue,
  onFalse,
  disabled,
  conflictMessage,
  onDismissConflict,
}: {
  t: TFunction<"map">;
  offered: AlertVerdict[];
  onTrue: () => void;
  onFalse: () => void;
  disabled: boolean;
  conflictMessage?: string | null;
  onDismissConflict?: () => void;
}) => {
  // A decided alert offers only the OTHER verdict, so the label says what the
  // press does — "Change to False" is a correction, "False" is a first call.
  const correcting = offered.length === 1;
  return (
    <div className="pointer-events-auto absolute bottom-0 left-1/2 flex -translate-x-1/2 flex-col items-center gap-2">
      {conflictMessage ? (
        <div
          role="alert"
          className="flex max-w-xl items-start gap-3 rounded-lg border border-amber-400/60 bg-card px-3 py-2 text-sm shadow-lg"
        >
          <span className="text-foreground">{conflictMessage}</span>
          {onDismissConflict ? (
            <button
              type="button"
              onClick={onDismissConflict}
              className="shrink-0 text-muted-foreground hover:text-foreground"
            >
              {t("alertReview.dismiss")}
            </button>
          ) : null}
        </div>
      ) : null}
      <div className="flex gap-3">
        {offered.includes("resolved") ? (
          <VerdictButton
            onClick={onTrue}
            disabled={disabled}
            hint="T"
            icon={<Check className="h-5 w-5" />}
            className="border-amber-400 bg-amber-400 text-amber-950 hover:bg-amber-300"
          >
            {correcting ? t("alertReview.changeToTrue") : t("alertReview.true")}
          </VerdictButton>
        ) : null}
        {offered.includes("false") ? (
          <VerdictButton
            onClick={onFalse}
            disabled={disabled}
            hint="F"
            icon={<X className="h-5 w-5" />}
            className="border-border bg-card text-foreground hover:bg-muted"
          >
            {correcting ? t("alertReview.changeToFalse") : t("alertReview.false")}
          </VerdictButton>
        ) : null}
      </div>
    </div>
  );
};

const VerdictButton = ({
  onClick,
  disabled,
  hint,
  icon,
  className,
  children,
}: {
  onClick: () => void;
  disabled: boolean;
  hint: string;
  icon: ReactNode;
  className?: string;
  children: ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={cn(
      "flex h-[52px] min-w-[208px] items-center justify-center gap-2.5 rounded-lg border px-6",
      "text-[15px] font-semibold shadow-lg transition-colors disabled:opacity-60",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      className,
    )}
  >
    {icon}
    {children}
    <kbd className="ml-1 rounded bg-black/10 px-1.5 py-0.5 font-mono text-[11px] opacity-70">
      {hint}
    </kbd>
  </button>
);

/**
 * Drag from the panel's inner corner. Both panels take the same size, so this
 * drives both — see `useAlertReviewLayoutStore`.
 */
const ResizeHandle = ({
  t,
  onResize,
  onReset,
}: {
  t: TFunction<"map">;
  onResize: (dx: number, dy: number) => void;
  onReset: () => void;
}) => {
  const from = useRef<{ x: number; y: number } | null>(null);
  return (
    <button
      type="button"
      aria-label={t("alertReview.resize")}
      title={t("alertReview.resizeTitle")}
      onDoubleClick={onReset}
      onPointerDown={(e) => {
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        from.current = { x: e.clientX, y: e.clientY };
      }}
      onPointerMove={(e) => {
        if (!from.current) return;
        onResize(e.clientX - from.current.x, e.clientY - from.current.y);
        from.current = { x: e.clientX, y: e.clientY };
      }}
      onPointerUp={() => {
        from.current = null;
      }}
      onKeyDown={(e) => {
        const step = e.shiftKey ? 64 : 16;
        if (e.key === "ArrowLeft") onResize(-step, 0);
        else if (e.key === "ArrowRight") onResize(step, 0);
        else if (e.key === "ArrowDown") onResize(0, step);
        else if (e.key === "ArrowUp") onResize(0, -step);
        else return;
        e.preventDefault();
        e.stopPropagation();
      }}
      className={cn(
        "absolute bottom-2 left-2 flex h-6 w-6 cursor-nesw-resize items-center justify-center",
        "rounded-md border border-white/25 bg-black/60 text-white/80",
        "hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400",
      )}
    >
      <svg viewBox="0 0 12 12" className="h-3 w-3" aria-hidden="true">
        <path d="M11 1 1 11M11 5v6H5" stroke="currentColor" strokeWidth="1.5" fill="none" />
      </svg>
    </button>
  );
};

export default AlertReviewV2;
